<script lang="ts">
  import { progressBreakdown } from '../lib/logic/progressBreakdown';
  import type { StackMenuPr } from '../lib/logic/prStack';
  import { prPath, type PrRoute, type PrTab } from '../lib/router';
  import { onDestroy, untrack } from 'svelte';
  import { engineDegraded } from '../lib/diff/engine';
  import { acquirePrStore, releasePrStore, type Subject } from '../lib/state/pr.svelte';
  import { FailureToast } from '../lib/state/failureToast';
  import PaneBoundary from '../lib/components/PaneBoundary.svelte';
  import { DiffModel } from '../lib/state/diffs.svelte';
  import { diffModelReview, fullyReviewed } from '../lib/state/fileReviewPort';
  import { nav } from '../lib/nav.svelte';
  import { reviewProgress } from '../lib/logic/browse';
  import { summarize } from '../lib/logic/prSummary';
  import { prSummaries } from '../lib/state/prSummaries.svelte';
  import { carryFilter, readFilter } from '../lib/logic/filefilter';
  import { PR_GUIDE } from '../lib/keys/guides';
  import { carryOut, keyboard, useKeys } from '../lib/keys/keyboard.svelte';
  import { updatePage, type PageEffect, type PageStack } from '../lib/commands/page';
  import { fileTypes } from '../lib/state/filetypes.svelte';
  import Rail from '../lib/components/Rail.svelte';
  import PrHeader from '../lib/components/PrHeader.svelte';
  import Overview from './Overview.svelte';
  import Files from './Files.svelte';
  import Commits from './Commits.svelte';
  import Review from './Review.svelte';
  import RangeTimeline from '../lib/components/RangeTimeline.svelte';
  import ErrorNotice from '../lib/components/ErrorNotice.svelte';
  import { prefs } from '../lib/state/prefs.svelte';
  import { settings } from '../lib/state/settings.svelte';
  import { algorithmOf } from '../lib/diff/algorithms';
  import { COMMENT_PARAM, THREAD_PARAM, jumpStep, nextUnreviewedFile } from '../lib/logic/headerTargets';
  import { LINE_PARAM, sameCommit, showsComparison } from '../lib/logic/snapshotLink';
  import { reveal } from '../lib/state/reveal.svelte';
  import { installLinkText } from '../lib/api/errors';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import { toast } from '../lib/state/toast.svelte';
  import FindFile from '../lib/components/FindFile.svelte';
  import { findFile } from '../lib/state/findFile.svelte';
  import { session } from '../lib/state/session.svelte';

  interface Props {
    route: PrRoute;
  }
  let { route }: Props = $props();

  // svelte-ignore state_referenced_locally
  const pr = acquirePrStore(route.owner, route.repo, route.number);
  onDestroy(() => {
    releasePrStore(pr);
    toast.dismiss();
  });

  // Failed writes are told in the toast, with Retry (see FailureToast), but one whose box is open and tells it itself.
  const failureToast = new FailureToast<Subject>(toast);
  $effect(() => {
    const live = pr.writes.untold;
    // Read so the toast closing (dismissed or timed out) re-syncs: that gives its failure up.
    void toast.message;
    untrack(() => failureToast.sync(live));
  });
  const tab = $derived<PrTab>(route.name);

  const reviewHref = () => carryFilter(prPath(pr.owner, pr.repo, pr.number, 'review'), nav.query);

  /** The Review view (the header's button, `a`); on it, its publish button (else its verdict) takes focus. */
  function toPublish() {
    if (tab !== 'review') return nav.go(reviewHref());
    const go = document.querySelector<HTMLButtonElement>('.publish-go');
    if (go && !go.disabled) go.focus();
    else document.querySelector<HTMLElement>('[role=radiogroup] input:checked')?.focus();
  }

  // A hand-off write that fails while the Review view is not shown is told
  // here, once, with the way to its Retry (offered only on that page).
  let toldFailures = untrack(() => pr.aftermath.failures);
  $effect(() => {
    const n = pr.aftermath.failures;
    if (n <= toldFailures) return;
    toldFailures = n;
    if (untrack(() => tab) === 'review') return;
    untrack(() =>
      toast.show({
        text: 'The hand-off after your publish did not finish.',
        action: { label: 'Open Review', run: () => nav.go(reviewHref()) },
      }),
    );
  });

  const TAB_TITLES: Record<PrTab, string> = { overview: '', files: 'Files · ', commits: 'Snapshots · ', review: 'Review · ' };

  // One diff model per comparison (from/to/whitespace/algorithm), shared by the views.
  // A new comparison loads beside the shown one, which stays on screen
  // (and in the Files view, mounted) until the new one has its file list
  // and the open file ready; then it takes over in place.
  const from = $derived(nav.query.get('from'));
  const to = $derived(nav.query.get('to'));
  const ws = $derived(nav.query.get('ws') === 'ignore');
  const algorithm = $derived(algorithmOf(prefs.value.diffAlgorithm));
  const ready = $derived(pr.status === 'ready' && !!pr.pr);
  let model: DiffModel | null = $state(null);
  /** The comparison being loaded to replace `model`. */
  let next: DiffModel | null = $state(null);
  let modelKey = '';
  /** Key of the comparison `model` shows. */
  let shownKey = '';
  // Bumped to rebuild the diff after new commits were pushed.
  let diffRevision = $state(0);
  const headMoved = $derived.by(() => {
    const m = model as DiffModel | null;
    return !!m?.head && !!pr.pr && m.head !== pr.pr.headRefOid;
  });
  /** Most time a new comparison waits for the open file before it is shown anyway. */
  const FILE_WAIT_MS = 15000;
  $effect(() => {
    if (!ready) return;
    const key = `${from}|${to}|${ws}|${algorithm}|${diffRevision}`;
    if (key === modelKey && model) return;
    modelKey = key;
    if (key === shownKey && model) {
      // Back to the comparison on screen before the new one loaded.
      untrack(() => next?.dispose());
      next = null;
      return;
    }
    const m = new DiffModel(pr, { from, to, ignoreWhitespace: ws, algorithm });
    untrack(() => {
      next?.dispose();
      if (!model) {
        model = m;
        shownKey = key;
        void m.init();
        return;
      }
      next = m;
      void (async () => {
        await m.init();
        // The open file, so the reader swaps content without a loading gap.
        const file = nav.query.get('file');
        if (m === next && m.status === 'ready' && file) {
          m.open(file);
          await Promise.race([m.settled(file), new Promise((r) => setTimeout(r, FILE_WAIT_MS))]);
        }
        if (m !== next) return;
        const old = model;
        model = m;
        shownKey = key;
        next = null;
        old?.dispose();
      })();
    });
  });
  onDestroy(() => {
    next?.dispose();
    model?.dispose();
    whole?.dispose();
  });

  // Beside another comparison than base…head, the pull request's own
  // (base…head) is kept too, never shown: the whole pull request's review
  // count comes from it. It starts from the statuses a base…head view
  // kept, and counts the remaining files in the background once the
  // comparison on screen has its own.
  let whole: DiffModel | null = $state(null);
  let wholeKey = '';
  $effect(() => {
    const m = model as DiffModel | null;
    const key = ready && m?.status === 'ready' && m.interdiff ? `${ws}|${algorithm}|${diffRevision}` : '';
    if (key === wholeKey) return;
    wholeKey = key;
    untrack(() => {
      whole?.dispose();
      whole = key ? new DiffModel(pr, { from: null, to: null, ignoreWhitespace: ws, algorithm }) : null;
      void whole?.init();
    });
  });
  $effect(() => {
    const w = whole as DiffModel | null;
    const m = model as DiffModel | null;
    if (!w || w.status !== 'ready' || !m?.allSettled) return;
    untrack(() => w.prefetch(w.entries.map((e) => e.path)));
  });

  // The review model follows the marks, for the comparison shown and the
  // one loading to replace it (so it opens with reviewed blocks folded).
  $effect(() => {
    const anchors = pr.marks.anchors;
    for (const m of [model, next, whole] as (DiffModel | null)[]) if (m) m.setMarks(anchors);
  });

  // Key-based marks from before anchors resolve file by file, in whatever
  // comparison shows them (docs/design.md "Review marks"): each model looks
  // for the pending keys as its files load and hands what they cover to the
  // store, which records it once (the server keeps the first resolution).
  $effect(() => {
    const keys = pr.marks.legacy;
    for (const m of [model, next, whole] as (DiffModel | null)[]) {
      if (!m) continue;
      m.onResolved ??= (r) => pr.marks.resolve(r);
      m.setLegacy(keys);
    }
  });

  // GitHub's "Viewed" checkbox follows the review marks of the files at the
  // head, judged by a base…head model there (none while another comparison
  // is shown, or marks are off).
  $effect(() => {
    const on = settings.viewedSync;
    untrack(() => pr.setViewedSync(on));
  });
  // What the Settings page says of the sync: GitHub refusing it here, or files that failed.
  $effect(() => {
    const health = { pr: `${pr.owner}/${pr.repo}#${pr.number}`, path: prPath(pr.owner, pr.repo, pr.number, 'files'), paused: pr.viewed.enabled ? pr.viewed.paused : null, errors: pr.viewed.errors };
    untrack(() => settings.reportViewed(health));
  });
  $effect(() => {
    const m = model as DiffModel | null;
    const head = pr.pr?.headRefOid;
    const judged = !!m && m.status === 'ready' && !m.interdiff && !!head && !!m.head && sameCommit(m.head, head) && pr.serverData && pr.marks.loaded && !engineDegraded();
    if (!m || !judged || !head || !pr.viewed.enabled) {
      untrack(() => pr.viewed.observe(null, {}));
      return;
    }
    const local: Record<string, boolean | null> = {};
    for (const e of m.entries) local[e.path] = fullyReviewed(m, e.path);
    untrack(() => pr.viewed.observe(diffModelReview(m, pr.marks, head), local));
  });

  // New comments anchor to the diff only while the Files view shows it;
  // elsewhere they go on the current head.
  $effect(() => {
    const m = model as DiffModel | null;
    if (tab !== 'files' || !m) return;
    untrack(() => m.show());
    return () => m.hide();
  });

  // The range picker shows once there is more than base and one commit, or a range off the default.
  const showTimeline = $derived(pr.snapshots.length > 1 || nav.query.has('from') || nav.query.has('to'));

  /** The snapshot the Files view's new side shows, when the comparison ends before the head. */
  const pastSnapshot = $derived.by(() => {
    const m = model as DiffModel | null;
    const head = pr.pr?.headRefOid ?? null;
    const shown = m?.status === 'ready' && m.interdiff ? m.newCommit : null;
    if (!shown || !head || sameCommit(shown, head)) return null;
    const s = pr.snapshots.find((x) => sameCommit(x.sha, shown));
    return { n: s?.n ?? null, short: s?.short ?? shown.slice(0, 7) };
  });
  /** The snapshot bar's height, which the explorer pins below (it wraps on narrower screens). */
  let snapshotBarH = $state(0);
  /** The Files view's file at the head: the query without the comparison's end or a pending deep link. */
  const headHref = $derived.by(() => {
    const q = new URLSearchParams(nav.query);
    for (const k of ['to', LINE_PARAM, THREAD_PARAM, COMMENT_PARAM]) q.delete(k);
    const qs = q.toString();
    return prPath(pr.owner, pr.repo, pr.number, 'files') + (qs ? '?' + qs : '');
  });

  // Review progress over the changed files the URL's file filter (types, path) keeps.
  const filter = $derived(readFilter(nav.query, fileTypes.classifier));
  const progress = $derived.by(() => {
    const m = model as DiffModel | null;
    if (!m || m.status !== 'ready') return null;
    return reviewProgress(
      m.entries.map((e) => e.path),
      (p) => m.progress(p),
      filter.active ? filter.test : undefined,
    );
  });

  /**
   * The whole pull request's review progress (base…head) under the same
   * file filter: `progress` itself in base…head; beside another
   * comparison, the kept base…head model's (`exact` false while some of
   * its files are still counted from GitHub's sizes), null until it is
   * ready.
   */
  const wholeProgress = $derived.by(() => {
    const m = model as DiffModel | null;
    if (!m || m.status !== 'ready' || !progress) return null;
    if (!m.interdiff) return { ...progress, exact: true };
    const w = whole as DiffModel | null;
    if (!w || w.status !== 'ready') return null;
    const paths = w.entries.map((e) => e.path);
    const pass = filter.active ? filter.test : undefined;
    return { ...reviewProgress(paths, (p) => w.progress(p), pass), exact: paths.every((p) => (pass && !pass(p)) || w.progress(p).exact) };
  });
  /** The name of the comparison shown when it is not the pull request's own; null for base…head. */
  const comparison = $derived.by(() => {
    const m = model as DiffModel | null;
    return m?.status === 'ready' && m.interdiff ? m.label : null;
  });

  // Record the pull request's size and the viewer's progress at its head, for the dashboard.
  $effect(() => {
    const m = model as DiffModel | null;
    const head = pr.pr?.headRefOid;
    if (!m || m.status !== 'ready' || m.interdiff || !head || !m.newCommit || !sameCommit(m.newCommit, head)) return;
    const counts = summarize(
      m.entries,
      (p) => m.progress(p),
      (p) => m.diffs[p]?.set?.blocks.filter((b) => b.kind === 'change').length ?? null,
    );
    untrack(() => prSummaries.record(`${pr.owner}/${pr.repo}`, pr.number, head, counts));
  });

  $effect(() => {
    const title = pr.pr?.title;
    document.title = title
      ? `${TAB_TITLES[tab]}${title} · #${pr.number}`
      : `${pr.owner}/${pr.repo}#${pr.number}`;
  });

  /** The review meter's breakdown by file, over the files the file filter keeps. */
  function breakdown() {
    const m = model as DiffModel | null;
    if (!m || m.status !== 'ready') return { done: 0, total: 0, filtered: filter.active, files: [] };
    return progressBreakdown(
      m.entries.map((e) => e.path),
      (p) => m.progress(p),
      (p) => m.diffs[p]?.set?.blocks ?? null,
      (p) => m.coverage(p),
      filter.active ? filter.test : undefined,
    );
  }

  // The review meter: turn on the Unreviewed filter and open the next file with unreviewed lines.
  function showUnreviewed() {
    prefs.set('unreviewedOnly', true);
    const m = model as DiffModel | null;
    if (!m || m.status !== 'ready') return;
    const paths = m.entries.map((e) => e.path).filter((p) => !filter.active || filter.test(p));
    const next = nextUnreviewedFile(paths, (p) => m.progress(p), nav.query.get('file'));
    if (next && next !== nav.query.get('file')) nav.setQuery({ file: next, line: null });
  }

  // ?thread=<id>[&comment=<id>] (the header's unresolved items, the
  // Overview's Links card): once the Files view shows that thread, open it
  // if it is collapsed (resolved), scroll it (or the comment named) into view,
  // highlight the thread briefly and focus what was scrolled to.
  $effect(() => {
    const id = nav.query.get(THREAD_PARAM);
    if (!id || tab !== 'files' || !ready) return;
    const commentId = nav.query.get(COMMENT_PARAM);
    const request = reveal.show(id);
    let stop = false;
    // The wait starts once the comparison the link names is on screen.
    let started = Date.now();
    /** When the thread was first found, with the line scroll done (its comment may take a moment more). */
    let threadAt: number | null = null;
    const done = () => {
      reveal.done(request);
      nav.setQuery({ [THREAD_PARAM]: null, [COMMENT_PARAM]: null }, { replace: true });
    };
    const find = () => {
      if (stop) return;
      const el = document.querySelector<HTMLElement>(`[data-thread="${CSS.escape(id)}"]`);
      const comment = commentId ? (el?.querySelector<HTMLElement>(`[data-comment="${CSS.escape(commentId)}"]`) ?? null) : null;
      const now = Date.now();
      // The comparison shown may be the one the link replaces, still showing
      // the thread elsewhere while the new one loads (see `showsComparison`).
      const shown = showsComparison((model as DiffModel | null)?.mode ?? null, nav.query, !!next);
      if (!shown) started = now;
      const settling = !!nav.query.get('line');
      if (el && shown && !settling && threadAt === null) threadAt = now;
      // Wait for the file's diff (and the line deep link's own scroll) to settle, and for the thread to open.
      const step = jumpStep({
        thread: el,
        comment,
        wantComment: !!commentId,
        comparisonShown: shown,
        settling,
        elapsed: now - started,
        threadElapsed: threadAt === null ? null : now - threadAt,
      });
      if (step.kind === 'wait') {
        setTimeout(find, 120);
        return;
      }
      if (step.kind === 'give-up' || !el) {
        done();
        return;
      }
      const target = step.target;
      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      // As little as shows it: a line deep link has already put its lines at
      // the top of the reader, with the thread under them, and the file's
      // header (which says which snapshot this is) stays in view.
      target.scrollIntoView({ block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
      el.classList.add('thread-flash');
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      setTimeout(() => el.classList.remove('thread-flash'), 1800);
      done();
    };
    setTimeout(find, 0);
    return () => {
      stop = true;
      reveal.done(request);
    };
  });

  /** Whether the header's stack menu is open (the stack command opens it). */
  let stackOpen = $state(false);
  /** The stack as the page's commands read it: the pull requests a stack motion steps through, nearest first, by `owner/repo#number`. */
  const stackId = (p: { owner: string; repo: string; number: number }) => `${p.owner}/${p.repo}#${p.number}`;
  const pageStack = $derived.by((): PageStack => {
    const s = pr.stack;
    if (s.tag === 'Failed') return { is: 'failed' };
    if (s.tag !== 'Has') return { is: 'loading' };
    const menu = s.value;
    if (!menu) return { is: 'none' };
    // The nearest in each direction is the menu's own; further steps follow the stack's line past it.
    const line = menu.rows.filter((r): r is StackMenuPr => r.kind !== 'gap' && r.lane === 0);
    const below = menu.below ? [menu.below, ...line.filter((r) => r.index < menu.below!.index).reverse()] : [];
    const above = menu.above ? [menu.above, ...line.filter((r) => r.index > menu.above!.index)] : [];
    return { is: 'known', below: below.map(stackId), above: above.map(stackId) };
  });
  const stackPr = (id: string) => (pr.stack.tag === 'Has' ? pr.stack.value?.rows.find((r): r is StackMenuPr => r.kind !== 'gap' && stackId(r) === id) : undefined);

  /** The page's content, where Find file reads selections. */
  let mainEl: HTMLElement | undefined = $state();
  /** Files of the pull request present at its head. */
  const prFiles = $derived(new Set((pr.files ?? []).filter((f) => f.status !== 'removed').map((f) => f.filename)));

  // The page scope: the grammar and the keys every view of the pull request shares (views register their own on
  // top), and the commands no view answers.
  useKeys({
    scope: 'pullRequest',
    guide: PR_GUIDE,
    handle: (command) => carryOut(updatePage({ tab, readOnly: pr.readOnly ? (pr.readOnlyText ?? 'Read-only') : null, stack: pageStack }, command)[1], perform),
  });

  function perform(e: PageEffect) {
    switch (e.kind) {
      case 'navigate':
        return e.tab === 'review' ? toPublish() : nav.go(carryFilter(prPath(pr.owner, pr.repo, pr.number, e.tab), nav.query));
      case 'focus-publish':
        return toPublish();
      case 'scroll-window':
        return window.scrollTo({ top: e.to === 'top' ? 0 : document.body.scrollHeight, behavior: 'instant' });
      case 'find-file':
        return findFile.fromSelection();
      case 'show-guide':
        return keyboard.showGuide();
      case 'open-stack':
        return void (stackOpen = true);
      case 'open-pr': {
        const to = stackPr(e.id);
        if (to) nav.go(prPath(to.owner, to.repo, to.number, tab));
        return;
      }
      case 'read-only':
        return void toast.show({ text: pr.readOnlyText ?? 'Read-only', link: pr.installLink, linkText: installLinkText(pr.owner) });
    }
  }
</script>

<div class="shell">
  <Rail {pr} {tab} progress={pr.serverData ? wholeProgress : null} counting={!!comparison && pr.serverData && !wholeProgress?.exact} />
  <main bind:this={mainEl} style:--snapshot-bar-h={snapshotBarH ? `${snapshotBarH}px` : null}>
    <!-- The load, and why it stopped when it did: the loading screen says both (the page appears once the pull request is in). -->
    <LoadingGate phase={pr.status === 'loading' ? 'loading' : pr.status === 'error' ? 'failed' : 'ready'} view={pr.loadView(tab)} onretry={() => pr.load()}>
    {#if pr.status === 'notfound'}
      <div class="center-msg">
        <h1>Pull request not found</h1>
        <p class="muted">
          {pr.owner}/{pr.repo}#{pr.number} doesn't exist, or GitHub doesn't show it to you. Changeling sees exactly what
          your GitHub account can see.
        </p>
        <a class="btn" href="/">Go to the dashboard</a>
      </div>
    {:else if pr.status === 'revoked'}
      <div class="center-msg">
        <h1>You no longer have access to this repository</h1>
        <p class="muted">
          GitHub no longer shows {pr.owner}/{pr.repo} to your account, so Changeling has stopped showing this pull
          request.
        </p>
        <a class="btn" href="/">Go to the dashboard</a>
      </div>
    {:else if pr.pr}
      {#if pr.banner}
        {@const banner = pr.banner}
        <div class="banner readonly" role="status">
          {banner.text.join(' ')}
          {#if banner.install && pr.installLink}<a href={pr.installLink} target="_blank" rel="noreferrer" data-external>{installLinkText(pr.owner)} ↗</a>{/if}
          {#if banner.reload}<button class="btn small" onclick={() => location.reload()}>Reload</button>{/if}
        </div>
      {/if}
      <!-- The header and each view stop on their own (PaneBoundary): an error in one does not end the others' updates. -->
      <PaneBoundary what="the pull request's header">
        <PrHeader
          {pr}
          compact={tab === 'files'}
          progress={tab === 'files' && pr.serverData ? progress : null}
          {comparison}
          onpublish={toPublish}
          onunreviewed={showUnreviewed}
          {breakdown}
          publishKey={tab !== 'review'}
          {tab}
          bind:stackOpen
        />
      </PaneBoundary>
      {#if tab === 'overview'}
        <PaneBoundary what="the overview"><Overview {pr} /></PaneBoundary>
      {:else if tab === 'commits'}
        <PaneBoundary what="the snapshots"><Commits {pr} /></PaneBoundary>
      {:else if tab === 'review'}
        <PaneBoundary what="the review"><Review {pr} /></PaneBoundary>
      {:else if model}
        {#if headMoved}
          <div class="banner" role="status">
            New commits were pushed since this diff was loaded. Comments you add still go on the lines shown here.
            <button class="btn small" onclick={() => diffRevision++}>Reload diff</button>
          </div>
        {/if}
        {#if pr.filesMissing}
          <div class="banner" role="status">
            GitHub lists at most 3,000 files; {pr.filesMissing} changed files are not shown.
          </div>
        {/if}
        <!-- Outside the per-model key: picking a range keeps the timeline (focus, scroll). -->
        {#if showTimeline}<RangeTimeline {pr} />{/if}
        {#if pastSnapshot}
          <div class="snapshot-bar" role="status" bind:offsetHeight={snapshotBarH}>
            <span
              >Showing <b>{pastSnapshot.n === null ? '' : `snapshot ${pastSnapshot.n} · `}{pastSnapshot.short}</b>, not the head.
              Threads written on it sit on their original lines.</span
            >
            <a href={headHref}>Back to head</a>
          </div>
        {/if}
        <!-- Not keyed by model: a new comparison updates the reader in place (file, cursor, scroll, focus stay). -->
        <PaneBoundary what="the files"><Files {pr} {model} updating={!!next} whole={comparison ? wholeProgress : undefined} /></PaneBoundary>
      {/if}
    {/if}
    </LoadingGate>
  </main>
</div>

{#if pr.pr}
  <FindFile
    owner={pr.owner}
    repo={pr.repo}
    number={pr.number}
    tree={model?.repoTree ?? null}
    changed={prFiles}
    webUrl={session.config?.github_web_url ?? 'https://github.com'}
    root={mainEl ?? null}
  />
{/if}

<!-- How each write ended, announced politely (failures also come as the toast below). Keyed, so the same words are announced again. -->
<div class="sr-only" role="status" aria-live="polite">
  {#key pr.writes.news?.id}{pr.writes.news?.text ?? ''}{/key}
</div>
<!-- The live region is always in the page, so screen readers announce the toast put into it. Toasts are failures. -->
<div class="toast-region" role="alert" aria-live="assertive">
  {#if toast.message}
    <!-- svelte-ignore a11y_no_static_element_interactions (hover only holds the timer; the button dismisses) -->
    <div
      class="toast"
      onmouseenter={() => toast.hold()}
      onmouseleave={() => toast.release()}
      onfocusin={() => toast.hold()}
      onfocusout={(e) => {
        if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) toast.release();
      }}
    >
      <span class="toast-text"><ErrorNotice text={toast.message.text} link={toast.message.link} linkText={toast.message.linkText} /></span>
      {#if toast.message.action}<button class="toast-action" onclick={() => toast.act()}>{toast.message.action.label}</button>{/if}
      <button class="toast-x" aria-label="Dismiss" title="Dismiss" onclick={() => toast.dismiss()}>×</button>
    </div>
  {/if}
</div>

<style>
  .banner.readonly {
    padding: 8px 14px;
    margin-bottom: 14px;
    font-size: 13px;
    background: var(--warnbg);
    border-left-color: var(--warn);
    color: var(--fg);
  }
  .banner.readonly a {
    color: var(--fg);
    font-weight: 650;
  }
  .toast {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }
  .toast-text {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .toast-x {
    border: 0;
    background: none;
    color: inherit;
    opacity: 0.7;
    font-size: 16px;
    line-height: 1;
    padding: 0 2px;
  }
  .toast-action {
    flex: none;
    border: 1px solid currentColor;
    border-radius: 5px;
    background: none;
    color: inherit;
    font-size: 12px;
    font-weight: 650;
    padding: 1px 9px;
  }
  .toast-action:focus-visible {
    outline-color: var(--bg);
  }
  .toast-x:hover {
    opacity: 1;
  }
  .toast-x:focus-visible {
    outline-color: var(--bg);
  }
  :global(.thread.thread-flash) {
    animation: thread-flash 1.8s ease-out;
  }
  @keyframes thread-flash {
    0%,
    35% {
      box-shadow: 0 0 0 3px var(--accent);
    }
    100% {
      box-shadow: 0 0 0 3px transparent;
    }
  }
  :global(.thread:focus) {
    outline: none;
  }
</style>
